<template>
  <!-- Comments v3 (spec 2026-10-18-comments-design, plan 1): the page's threads in the shown language. Posts go
       through the addComment action; edit, delete and resolve are plain Hasura writes. -->
  <aside
    v-if="visible" :class="['comments-panel', { 'in-preview': topSelector !== '.header' }]" data-test="comments-panel"
    :style="{ top: `${top}px` }"
    :aria-label="$t('webmag.comments.title')"
  >
    <!-- Plan 3b polish: the look of the Snippet settings (sticky head, small tab switch, bars). -->
    <header class="comments-head">
      <h2 class="comments-title">{{ $t('webmag.comments.title') }}</h2>
      <div class="comments-tabs" role="tablist">
        <button
          type="button" role="tab" data-test="comments-tab-open"
          :class="['comments-tab', { active: tab === 'open' }]" :aria-selected="tab === 'open'" @click="tab = 'open'"
        >{{ $t('webmag.comments.open') }} <span class="comments-tab-n">{{ openThreads.length }}</span></button>
        <button
          type="button" role="tab" data-test="comments-tab-resolved"
          :class="['comments-tab', { active: tab === 'resolved' }]" :aria-selected="tab === 'resolved'" @click="tab = 'resolved'"
        >{{ $t('webmag.comments.resolved') }} <span class="comments-tab-n">{{ resolvedThreads.length }}</span></button>
      </div>
      <button type="button" class="comments-close" :title="$t('webmag.comments.hidePanel')" :aria-label="$t('webmag.comments.hidePanel')" @click="$emit('hidden')">×</button>
    </header>

    <!-- Founder 19.10.2026: filter by status and by person at the top; the person filter also works on Erledigt. -->
    <div class="comments-filter" role="group" :aria-label="$t('webmag.comments.filter')">
      <select v-if="tab === 'open'" v-model="filterStatus" data-test="comments-filter-status" :aria-label="$t('webmag.comments.statusLabel')">
        <option value="all">{{ $t('webmag.comments.filterAllStatus') }}</option>
        <option v-for="s in statuses" :key="s" :value="s">{{ $t(`webmag.comments.status.${s}`) }}</option>
      </select>
      <select v-model="filterAssignee" data-test="comments-filter-assignee" :aria-label="$t('webmag.comments.assignee')">
        <option value="all">{{ $t('webmag.comments.filterAllPeople') }}</option>
        <option value="mine">{{ $t('webmag.comments.mine') }}</option>
        <option value="none">{{ $t('webmag.comments.nobody') }}</option>
        <option v-for="m in members" :key="m.user_id" :value="String(m.user_id)">{{ m.full_name }}</option>
      </select>
    </div>

    <div v-if="tab === 'open'" class="comments-new">
      <!-- A place picked in the preview (plan 2); without one the comment is about the whole page. -->
      <p v-if="pendingAnchor" class="comments-pending" data-test="comment-pending">
        <span>{{ pendingAnchor.quote ? $t('webmag.comments.text') : $t('webmag.comments.snippet') }}</span>
        <q v-if="pendingAnchor.quote">{{ pendingAnchor.quote.exact }}</q>
        <button type="button" class="comments-link" :aria-label="$t('webmag.comments.close')" @click="$emit('anchor-used')">×</button>
      </p>
      <!-- Plan 3: on selected text, propose the new wording; the reason is optional then. -->
      <label v-if="suggestable" class="comments-suggest">
        <input v-model="suggesting" type="checkbox" data-test="comment-suggest" /> {{ $t('webmag.comments.suggest') }}
      </label>
      <textarea
        v-if="suggesting" v-model="newText" data-test="comment-new-text" class="comments-input" rows="2"
        :placeholder="$t('webmag.comments.newText')"
      ></textarea>
      <comment-composer
        ref="newInput" :page-id="pageId" :members="members" :busy="busy" :allow-empty="suggesting" test-id="comment-new"
        :send-label="$t('webmag.comments.send')"
        :placeholder="suggesting ? $t('webmag.comments.reason') : pendingAnchor ? $t('webmag.comments.newOnPlace') : $t('webmag.comments.newPlaceholder')"
        @submit="post"
      />
    </div>

    <div class="comments-list">
      <p v-if="!shown.length" class="comments-empty">{{ $t('webmag.comments.empty') }}</p>
      <article
        v-for="t in shown" :key="t.id" :ref="`thread-${t.id}`" data-test="comment-thread"
        :class="['comments-thread', { focused: t.id === focusId }]"
      >
        <div class="comments-thread-head">
          <span class="comments-number" data-test="thread-number">{{ t.number }}</span>
          <button
            v-if="place(t) === 'element'" type="button" class="comments-place link" data-test="comment-place-element"
            @click="$emit('focus-thread', t.id)"
          >{{ t.anchor.quote ? $t('webmag.comments.text') : $t('webmag.comments.snippet') }}</button>
          <span v-else-if="place(t) === 'gone'" class="comments-place gone" data-test="comment-place-gone">{{ $t('webmag.comments.gone') }}</span>
          <span v-else class="comments-place">{{ $t('webmag.comments.wholePage') }}</span>
          <span v-if="t.comment.device" class="comments-device" data-test="comment-device">{{ $t(`webmag.comments.on.${t.comment.device}`) }}</span>
          <template v-if="canResolve">
            <button
              v-if="!t.resolved" type="button" class="comments-icon resolve" data-test="comment-resolve"
              :title="$t('webmag.comments.resolve')" :aria-label="$t('webmag.comments.resolve')" @click="resolve(t, true)"
            >✓</button>
            <button
              v-else type="button" class="comments-icon" data-test="comment-reopen"
              :title="$t('webmag.comments.reopen')" :aria-label="$t('webmag.comments.reopen')" @click="resolve(t, false)"
            >↺</button>
          </template>
        </div>
        <!-- Plan 3c: status and assignee; edit right changes both, the assignee only the status. -->
        <div class="comments-state">
          <select
            v-if="!t.resolved && mayStatus(t)" :class="['comments-status', `s-${statusOf(t)}`]" data-test="comment-status"
            :value="statusOf(t)" :aria-label="$t('webmag.comments.statusLabel')" @change="setState(t, { status: $event.target.value })"
          >
            <option v-for="s in statuses" :key="s" :value="s">{{ $t(`webmag.comments.status.${s}`) }}</option>
          </select>
          <span v-else :class="['comments-status', `s-${statusOf(t)}`]" data-test="comment-status-text">{{ statusLabel(t) }}</span>
          <!-- Keyed on the member count: the people load after the first render, and a select keeps no value without its option. -->
          <select
            v-if="canResolve && !t.resolved" :key="`assignee-${t.id}-${members.length}`" class="comments-assignee" data-test="comment-assignee"
            :value="assigneeOf(t) || ''" :aria-label="$t('webmag.comments.assignee')"
            @change="setState(t, { assigneeId: $event.target.value ? Number($event.target.value) : null })"
          >
            <option value="">{{ $t('webmag.comments.nobody') }}</option>
            <option v-for="m in members" :key="m.user_id" :value="m.user_id">{{ m.full_name }}</option>
          </select>
          <span v-else-if="assigneeOf(t)" class="comments-assignee-text">{{ nameOf(assigneeOf(t)) }}</span>
        </div>
        <a v-if="t.comment.screenshot_url" :href="t.comment.screenshot_url" target="_blank" rel="noopener" class="comments-shot-link">
          <img :src="t.comment.screenshot_url" :alt="$t('webmag.comments.screenshot')" class="comments-shot" data-test="comment-screenshot" />
        </a>
        <p v-if="t.comment.setup" class="comments-setup" data-test="comment-setup">{{ setupLine(t.comment.setup) }}</p>
        <p v-if="t.comment.suggestion" class="comments-suggestion">
          <del data-test="suggestion-old">{{ t.comment.suggestion.old }}</del>
          <span aria-hidden="true">→</span>
          <ins data-test="suggestion-new">{{ t.comment.suggestion.new }}</ins>
        </p>
        <q v-else-if="t.anchor && t.anchor.quote" class="comments-quote">{{ t.anchor.quote.exact }}</q>
        <div v-if="t.comment.suggestion && !t.resolved && canResolve" class="comments-row">
          <button
            type="button" class="comments-send" data-test="suggestion-apply" :disabled="busy || staleIds.includes(t.id) || pendingIds.includes(t.id)"
            @click="$emit('apply', t)"
          >{{ $t('webmag.comments.apply') }}</button>
          <button type="button" class="comments-link" data-test="suggestion-reject" :disabled="pendingIds.includes(t.id)" @click="$emit('reject', t)">{{ $t('webmag.comments.reject') }}</button>
        </div>
        <p v-if="t.comment.suggestion && !t.resolved && staleIds.includes(t.id)" class="comments-stale" data-test="suggestion-stale">
          {{ $t('webmag.comments.stale') }}
        </p>
        <p
          v-if="t.comment.suggestion && t.resolved && t.comment.resolution && t.comment.resolution.outcome !== 'resolved'"
          class="comments-outcome" data-test="suggestion-outcome"
        >{{ t.comment.resolution.outcome === 'rejected' ? $t('webmag.comments.rejected') : $t('webmag.comments.applied') }}</p>

        <div
          v-for="c in [t.comment, ...(openReplies[t.id] ? t.replies : [])]" :key="c.comment_id"
          :class="['comments-post', { reply: c !== t.comment }]"
        >
          <div class="comments-meta">
            <span class="comments-author">{{ (c.author && c.author.full_name) || '–' }}</span>
            <span class="comments-time">{{ when(c.created_at) }}</span>
            <span v-if="c.edited_at" class="comments-time">· {{ $t('webmag.comments.edited') }}</span>
          </div>
          <template v-if="editing === c.comment_id">
            <textarea v-model="editText" class="comments-input" rows="2" data-test="comment-edit-input"></textarea>
            <div class="comments-row">
              <button type="button" class="comments-send" :disabled="busy || !editText.trim()" data-test="comment-edit-save" @click="saveEdit(c)">{{ $t('webmag.comments.save') }}</button>
              <button type="button" class="comments-link" @click="editing = null">{{ $t('webmag.cancel') }}</button>
            </div>
          </template>
          <p v-else-if="c.body" class="comments-body"><template v-for="(part, i) in partsOf(c)"><mark v-if="part.mention" :key="i" class="comments-mention">{{ part.text }}</mark><template v-else>{{ part.text }}</template></template></p>
          <ul v-if="c.attachments && c.attachments.length" class="comments-files">
            <li v-for="f in c.attachments" :key="f.url" data-test="comment-file">
              <a :href="f.url" target="_blank" rel="noopener">
                <img v-if="isImage(f.type)" :src="f.url" :alt="f.name" class="comments-thumb" data-test="comment-thumb" />
                <span v-else class="comments-chip" data-test="comment-chip">📄 {{ f.name }}</span>
              </a>
            </li>
          </ul>
          <div v-if="c.author_id === userId && editing !== c.comment_id" class="comments-row">
            <button type="button" class="comments-link" data-test="comment-edit" @click="startEdit(c)">{{ $t('webmag.comments.edit') }}</button>
            <!-- Deleting a comment would take the others' replies with it; then only editing is left. -->
            <button
              v-if="c !== t.comment || !t.replies.length" type="button" class="comments-link" data-test="comment-delete"
              @click="remove(c)"
            >{{ $t('webmag.comments.delete') }}</button>
          </div>
        </div>

        <button type="button" class="comments-replies-toggle" data-test="replies-toggle" @click="toggleReplies(t.id)">
          <span aria-hidden="true">{{ openReplies[t.id] ? '▾' : '▸' }}</span>
          <template v-if="t.replies.length">
            <span class="comments-replies-n">{{ t.replies.length }}</span>
            {{ $t(t.replies.length === 1 ? 'webmag.comments.oneReply' : 'webmag.comments.manyReplies') }}
          </template>
          <template v-else>{{ $t('webmag.comments.reply') }}</template>
        </button>
        <div v-if="openReplies[t.id] && !t.resolved" class="comments-reply">
          <comment-composer
            :page-id="pageId" :members="members" :busy="busy" :rows="1" test-id="comment-reply"
            :placeholder="$t('webmag.comments.reply')" :send-label="$t('webmag.comments.reply')"
            @submit="(payload, done) => reply(t, payload, done)"
          />
        </div>
      </article>
    </div>
  </aside>
</template>

<script>
import AddComment from '@/graphQlQueries/mutations/addComment';
import UpdatePageComment from '@/graphQlQueries/mutations/updatePageComment';
import DeletePageComment from '@/graphQlQueries/mutations/deletePageComment';
import InsertPageCommentResolution from '@/graphQlQueries/mutations/insertPageCommentResolution';
import DeletePageCommentResolution from '@/graphQlQueries/mutations/deletePageCommentResolution';
import SetCommentState from '@/graphQlQueries/mutations/setCommentState';
import {
  toThreads, placeLabel, canSuggest, statusOf,
} from '@/utils/comments';
import { setupLine } from '@/utils/clientSetup';
import { isImage } from '@/utils/commentFiles';
import { bodyParts } from '@/utils/mentions';
import CommentComposer from './CommentComposer.vue';

export default {
  name: 'CommentsPanel',
  components: { CommentComposer },
  props: {
    visible: { type: Boolean, default: false },
    rows: { type: Array, default: () => [] },
    languageId: { type: Number, default: null },
    pageId: { type: Number, required: true },
    userId: { type: Number, default: null },
    canResolve: { type: Boolean, default: false },
    // Section ids still on the page (from the canvas); null until it has answered.
    sectionIds: { type: Set, default: null },
    focusId: { type: Number, default: null },
    // Plan 2: a place picked in the preview and the preview's device; the new comment is about that place.
    pendingAnchor: { type: Object, default: null },
    device: { type: String, default: null },
    // What the panel sits under: the app header, or the preview's header.
    topSelector: { type: String, default: '.header' },
    // Plan 3: change requests whose passage no longer fits the text (asked from the canvas).
    staleIds: { type: Array, default: () => [] },
    // Change requests being applied or rejected right now.
    pendingIds: { type: Array, default: () => [] },
    // Plan 3b: the people who may be mentioned ({ user_id, full_name }).
    members: { type: Array, default: () => [] },
    // Plan 3c: () => ({ setup, shot }) for a new thread (the writer's setup and what the screenshot should show).
    capture: { type: Function, default: null },
  },
  data: () => ({
    tab: 'open',
    editing: null,
    editText: '',
    busy: false,
    suggesting: false,
    // Threads whose replies are unfolded (plan 3b polish).
    openReplies: {},
    newText: '',
    // Plan 3c: all | active | in_progress | in_review, and all | mine | none | a user id.
    filterStatus: 'all',
    filterAssignee: 'all',
    statuses: ['active', 'in_progress', 'in_review'],
    top: 56,
  }),
  computed: {
    suggestable() {
      return canSuggest(this.pendingAnchor);
    },
    threads() {
      return toThreads(this.rows, this.languageId);
    },
    openThreads() {
      return this.threads.filter((t) => !t.resolved);
    },
    resolvedThreads() {
      return this.threads.filter((t) => t.resolved);
    },
    pinIds() {
      return this.openThreads
        .filter((t) => (this.filterStatus === 'all' || statusOf(t) === this.filterStatus) && this.fitsAssignee(t))
        .map((t) => t.id);
    },
    shown() {
      const list = this.tab === 'open' ? this.openThreads : this.resolvedThreads;
      return list.filter((t) => this.fitsStatus(t) && this.fitsAssignee(t));
    },
  },
  watch: {
    // The pins on the page show what the list shows (small fixes 19.10.2026).
    pinIds: {
      immediate: true,
      handler(ids) {
        this.$emit('pin-ids', ids);
      },
    },
    visible: {
      immediate: true,
      handler(on) {
        // Below the app header, whose height changes with its buttons (it wraps when they do not fit).
        const header = on && document.querySelector(this.topSelector);
        if (header) this.top = Math.round(header.getBoundingClientRect().bottom);
      },
    },
    suggesting(on) {
      if (on && this.pendingAnchor && this.pendingAnchor.quote) this.newText = this.pendingAnchor.quote.exact;
    },
    pendingAnchor(anchor) {
      this.suggesting = false;
      if (anchor) this.$nextTick(() => this.$refs.newInput && this.$refs.newInput.focus());
    },
    focusId(id) {
      if (!id) return;
      const t = this.threads.find((x) => x.id === id);
      if (t) this.tab = t.resolved ? 'resolved' : 'open';
      // Plan 4: opened from the inbox or a mail, so the new reply is in view.
      this.$set(this.openReplies, id, true);
      this.$nextTick(() => {
        const el = this.$refs[`thread-${id}`];
        const node = Array.isArray(el) ? el[0] : el;
        if (node && node.scrollIntoView) node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    },
  },
  methods: {
    place(t) {
      return placeLabel(t, this.sectionIds);
    },
    when(at) {
      return new Date(at).toLocaleString(this.$i18n && this.$i18n.locale, { dateStyle: 'short', timeStyle: 'short' });
    },
    async run(fn) {
      this.busy = true;
      try {
        await fn();
        this.$emit('changed');
        return true;
      } catch (e) {
        this.$bvToast.toast(this.$t('webmag.comments.notSaved'), { variant: 'danger', solid: true });
        return false;
      } finally {
        this.busy = false;
      }
    },
    async add(object) {
      const { data } = await this.$apollo.mutate({ mutation: AddComment, variables: { object } });
      const out = data && data.addComment;
      if (!out || !out.ok) throw new Error((out && out.error) || 'failed');
    },
    async post({ body, mentions, attachments }, done = () => {}) {
      const object = {
        pageId: this.pageId, body, languageId: this.languageId, anchor: this.pendingAnchor, device: this.device, mentions, attachments,
      };
      if (this.suggesting && this.newText === this.pendingAnchor.quote.exact) {
        this.$bvToast.toast(this.$t('webmag.comments.sameText'), { variant: 'warning', solid: true });
        done(false);
        return;
      }
      const seen = this.capture ? this.capture() : {};
      if (seen.setup) object.setup = seen.setup;
      if (seen.shot) object.shot = seen.shot;
      if (this.suggesting) object.suggestion = { old: this.pendingAnchor.quote.exact, new: this.newText };
      const ok = await this.run(() => this.add(object));
      done(ok);
      if (!ok) return;
      this.suggesting = false;
      if (this.pendingAnchor) this.$emit('anchor-used');
    },
    async reply(t, { body, mentions, attachments }, done = () => {}) {
      const ok = await this.run(() => this.add({
        pageId: this.pageId, parentId: t.id, body, mentions, attachments,
      }));
      done(ok);
    },
    isImage,
    toggleReplies(id) {
      this.$set(this.openReplies, id, !this.openReplies[id]);
    },
    /** A post's text with its mentions marked; only people it really mentions (their ids are stored). */
    partsOf(c) {
      const people = this.members.filter((m) => (c.mentions || []).includes(m.user_id));
      return bodyParts(c.body, people);
    },
    startEdit(c) {
      this.editing = c.comment_id;
      this.editText = c.body;
    },
    async saveEdit(c) {
      const body = this.editText.trim();
      const ok = await this.run(() => this.$apollo.mutate({
        mutation: UpdatePageComment, variables: { commentId: c.comment_id, body, editedAt: new Date().toISOString() },
      }));
      if (ok) this.editing = null;
    },
    async remove(c) {
      const sure = await this.$bvModal.msgBoxConfirm(this.$t('webmag.comments.deleteConfirm'), {
        okVariant: 'danger', okTitle: this.$t('webmag.comments.delete'), cancelTitle: this.$t('webmag.cancel'),
      });
      if (!sure) return;
      await this.run(() => this.$apollo.mutate({ mutation: DeletePageComment, variables: { commentId: c.comment_id } }));
    },
    async resolve(t, done) {
      await this.run(async () => {
        // Reopened threads start over as Aktiv, the assignee stays (spec Rev. 4). Aktiv first: if removing Erledigt then
        // fails, the thread is still resolved, not half open (small fixes 19.10.2026).
        if (!done) await this.$apollo.mutate({ mutation: SetCommentState, variables: this.stateVars(t, { status: 'active' }) });
        await this.$apollo.mutate({
          mutation: done ? InsertPageCommentResolution : DeletePageCommentResolution,
          variables: done ? { commentId: t.id, outcome: 'resolved' } : { commentId: t.id },
        });
      });
    },
    statusOf,
    fitsStatus(t) {
      return this.tab !== 'open' || this.filterStatus === 'all' || statusOf(t) === this.filterStatus;
    },
    fitsAssignee(t) {
      const who = this.assigneeOf(t);
      if (this.filterAssignee === 'all') return true;
      if (this.filterAssignee === 'mine') return who !== null && who === this.userId;
      if (this.filterAssignee === 'none') return who === null;
      return String(who) === this.filterAssignee;
    },
    setupLine(setup) {
      return setupLine(setup, (k) => this.$t(k));
    },
    statusLabel(t) {
      const s = statusOf(t);
      return s === 'resolved' ? this.$t('webmag.comments.resolved') : this.$t(`webmag.comments.status.${s}`);
    },
    assigneeOf(t) {
      return (t.comment.state && t.comment.state.assignee_id) || null;
    },
    nameOf(userId) {
      const m = this.members.find((x) => x.user_id === userId);
      return m ? m.full_name : '–';
    },
    mayStatus(t) {
      return this.canResolve || (this.userId != null && this.assigneeOf(t) === this.userId);
    },
    stateVars(t, patch) {
      const cur = t.comment.state || {};
      return {
        commentId: t.id,
        status: patch.status || cur.status || 'active',
        assigneeId: 'assigneeId' in patch ? patch.assigneeId : (cur.assignee_id || null),
      };
    },
    async setState(t, patch) {
      try {
        await this.$apollo.mutate({ mutation: SetCommentState, variables: this.stateVars(t, patch) });
        this.$emit('changed');
      } catch (e) {
        // Hasura refuses an assignee who may not open the page (spec Rev. 4); say that instead of "nicht gespeichert".
        const noAccess = 'assigneeId' in patch && patch.assigneeId && /check constraint/.test(String(e && e.message));
        this.$bvToast.toast(this.$t(noAccess ? 'webmag.comments.assigneeNoAccess' : 'webmag.comments.notSaved'), { variant: 'danger', solid: true });
      }
    },
  },
};
</script>

<style scoped>
.comments-panel {
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 1040;
  display: flex;
  flex-direction: column;
  width: 380px;
  background: #fff;
  border-left: 1px solid #e5e7eb;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.08);
  font-size: 14px;
  color: #1f2937;
}

.comments-head {
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 8px 8px 14px;
  border-bottom: 1px solid #eef0f3;
  background: #fff;
}

.comments-title {
  margin: 0;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.comments-tabs {
  display: inline-flex;
  margin-right: auto;
  padding: 2px;
  border-radius: 6px;
  background: #f1f3f6;
}

.comments-tab {
  padding: 3px 10px;
  border: 0;
  border-radius: 4px;
  background: none;
  color: #5b6270;
  font-size: 11px;
}

.comments-tab.active {
  background: #fff;
  color: #111;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.comments-tab-n {
  margin-left: 2px;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
}

.comments-close {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 6px;
  background: none;
  font-size: 20px;
  line-height: 1;
  color: #4b5563;
}

.comments-close:hover {
  background: #f1f3f6;
}

.comments-new,
.comments-reply {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

/* The preview modal colours every "p" grey (.modal-dialog .modal-body p); the panel prefix outranks it. */

/* In the preview the panel fills the modal body's right side, below the modal header. */
.comments-panel.in-preview {
  position: absolute;
  top: 0 !important;
  z-index: 1060;
}

.comments-new {
  flex-wrap: wrap;
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.comments-reply {
  order: 2;
  margin-top: 8px;
  padding-left: 14px;
}

.comments-panel .comments-pending {
  display: flex;
  flex-basis: 100%;
  gap: 6px;
  align-items: baseline;
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.comments-panel .comments-pending q {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #1f2937;
  font-style: italic;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.comments-input {
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  resize: vertical;
  font: inherit;
}

.comments-send {
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  background: #1f2937;
  color: #fff;
  font-weight: 500;
  white-space: nowrap;
}

.comments-send:disabled {
  opacity: 0.4;
}

.comments-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 0 24px;
}

.comments-panel .comments-empty {
  margin: 24px 0;
  color: #9ca3af;
  text-align: center;
}

/* A column so the replies toggle (order 1) sits between the first post and the replies (order 2). */
.comments-thread {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 12px 14px;
  border-bottom: 1px solid #eef0f3;
}

.comments-thread.focused {
  background: #fffbea;
  box-shadow: inset 3px 0 0 #f5c518;
}

.comments-thread-head {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
}

/* Same yellow circle as the pin in the preview, so list and page match. */
.comments-number {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #f5c518;
  color: #111827;
  font-size: 11.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.comments-place {
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
}

.comments-place.link {
  padding: 0;
  border: 0;
  background: none;
  color: #2563eb;
}

.comments-device {
  font-size: 11.5px;
  color: #9ca3af;
}

/* The resolve button sits at the right end of the thread head. */
.comments-thread-head > .comments-icon {
  margin-left: auto;
}

.comments-place.gone {
  color: #b45309;
}

.comments-icon {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: none;
  color: #6b7280;
  font-size: 15px;
  line-height: 1;
}

.comments-icon:hover {
  background: #f1f3f6;
  color: #111827;
}

.comments-icon.resolve:hover {
  background: #ecfdf5;
  color: #047857;
}

/* Plan 3c: status chip (a select that looks like a tag), assignee, screenshot and setup line. */
.comments-state {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 8px;
}

.comments-status {
  padding: 2px 8px;
  border: 0;
  border-radius: 10px;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 18px;
}

select.comments-status {
  appearance: none;
  padding-right: 20px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath d='M2 4l3 3 3-3' fill='none' stroke='%236b7280' stroke-width='1.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
  cursor: pointer;
}

.comments-status.s-active {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.comments-status.s-in_progress {
  background-color: #fffbeb;
  color: #b45309;
}

.comments-status.s-in_review {
  background-color: #f5f3ff;
  color: #6d28d9;
}

.comments-status.s-resolved {
  background-color: #ecfdf5;
  color: #047857;
}

.comments-assignee {
  max-width: 180px;
  padding: 1px 6px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 12px;
}

.comments-assignee-text {
  font-size: 12px;
  color: #374151;
}

.comments-shot-link {
  display: block;
  margin-bottom: 6px;
}

.comments-shot {
  display: block;
  width: 100%;
  max-height: 180px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  object-fit: cover;
  object-position: top;
}

.comments-panel .comments-setup {
  margin: 0 0 8px;
  font-size: 11px;
  color: #9ca3af;
}

.comments-filter {
  display: flex;
  gap: 6px;
  padding: 8px 14px;
  border-bottom: 1px solid #eef0f3;
}

.comments-filter select {
  flex: 1;
  min-width: 0;
}

.comments-filter select {
  padding: 3px 6px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 12px;
}

.comments-replies-toggle {
  order: 1;
  align-self: flex-start;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: none;
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
}

.comments-replies-toggle:hover {
  color: #111827;
}

.comments-replies-n {
  font-variant-numeric: tabular-nums;
}

.comments-suggest {
  display: flex;
  flex-basis: 100%;
  gap: 6px;
  align-items: center;
  margin: 0;
  font-size: 13px;
  color: #374151;
}

.comments-panel .comments-suggestion {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: baseline;
  margin: 0 0 6px;
}

.comments-panel .comments-suggestion del {
  padding: 0 2px;
  border-radius: 3px;
  background: #fee2e2;
  color: #991b1b;
}

.comments-panel .comments-suggestion ins {
  padding: 0 2px;
  border-radius: 3px;
  background: #dcfce7;
  color: #166534;
  text-decoration: none;
}

.comments-panel .comments-stale {
  margin: 4px 0 0;
  font-size: 12px;
  color: #b45309;
}

.comments-panel .comments-outcome {
  margin: 4px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.comments-mention {
  padding: 0 2px;
  border-radius: 3px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 500;
}

.comments-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
}

.comments-thumb {
  display: block;
  width: 72px;
  height: 72px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
}

.comments-chip {
  display: inline-block;
  max-width: 220px;
  padding: 4px 10px;
  overflow: hidden;
  border-radius: 12px;
  background: #f3f4f6;
  color: #1f2937;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.comments-quote {
  display: block;
  margin-bottom: 6px;
  padding-left: 8px;
  border-left: 2px solid #e5e7eb;
  color: #6b7280;
  font-style: italic;
}

.comments-post.reply {
  order: 2;
  margin-top: 10px;
  padding-left: 12px;
  border-left: 2px solid #e5e7eb;
}

.comments-meta {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.comments-author {
  font-weight: 600;
}

.comments-time {
  font-size: 12px;
  color: #9ca3af;
}

.comments-panel .comments-body {
  margin: 2px 0 0;
  color: #1f2937;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.comments-row {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

.comments-link {
  padding: 0;
  border: 0;
  background: none;
  font-size: 12px;
  color: #6b7280;
}
</style>
